import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Building2, Mail, Settings, LogOut, ChevronDown, Plus, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import MobileNav from './MobileNav';
import NotificationBell from '../NotificationBell';
import api from '../../lib/api';
import { organizationAPI } from '../../lib/api';
import { toast } from 'sonner';

function UserInitials({ name }) {
  const initials = (name || 'U')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-stone-900 text-white flex items-center justify-center text-xs font-bold select-none" data-testid="user-avatar">
      {initials}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { org, entities, switchEntity, canCreateEntity, refreshOrg } = useOrg();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resending, setResending] = useState(false);
  const [showNewEntity, setShowNewEntity] = useState(false);
  const [newEntityName, setNewEntityName] = useState('');
  const [creatingEntity, setCreatingEntity] = useState(false);
  const [switching, setSwitching] = useState(false);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/upload') return 'Upload data';
    if (path === '/transactions') return 'Review Queue';
    if (path === '/journal') return 'Journal Entries';
    if (path.startsWith('/journal/')) return 'Journal Entries';
    if (path === '/accounts') return 'Chart of Accounts';
    if (path === '/invoices') return 'Invoices';
    if (path === '/customers') return 'Customers';
    if (path === '/reports') return 'Reporting & Analytics';
    if (path === '/settings') return 'Account';
    if (path === '/platform-admin') return 'Kaya Platform';
    if (path === '/gtm-admin') return 'Growth Engine';
    return 'kaya.ai';
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Verification email sent — check your inbox');
    } catch {
      toast.error('Failed to send verification email');
    }
    setResending(false);
  };

  const handleSwitchEntity = async (orgId) => {
    if (orgId === org?.id || switching) return;
    setSwitching(true);
    const ok = await switchEntity(orgId);
    if (ok) {
      toast.success('Switched entity');
      navigate('/dashboard');
    } else {
      toast.error('Failed to switch entity');
    }
    setSwitching(false);
  };

  const handleCreateEntity = async () => {
    if (!newEntityName.trim()) return toast.error('Entity name is required');
    setCreatingEntity(true);
    try {
      await organizationAPI.createEntity({ company_name: newEntityName.trim() });
      toast.success('Entity created');
      setShowNewEntity(false);
      setNewEntityName('');
      refreshOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create entity');
    }
    setCreatingEntity(false);
  };

  const hasMultipleEntities = entities.length > 1 || canCreateEntity;

  return (
    <>
      {user && user.email_verified === false && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-3" data-testid="verify-email-banner">
          <Mail className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-800">Please verify your email address.</p>
          <button
            onClick={handleResendVerification}
            disabled={resending}
            className="text-xs font-medium text-[#D94E18] hover:underline"
            data-testid="resend-verification-btn"
          >
            {resending ? 'Sending...' : 'Resend email'}
          </button>
        </div>
      )}
    <header className="h-16 md:h-16 border-b border-stone-200 bg-white sticky top-0 z-10">
      <div className="h-full px-4 md:px-6 flex items-center justify-between">
        {/* Mobile: logo + title */}
        <div className="flex items-center gap-3 md:hidden">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#D94E18] rounded flex items-center justify-center">
              <Building2 className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
            </div>
          </Link>
          <h1 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {getPageTitle()}
          </h1>
        </div>

        {/* Desktop: page title + entity switcher */}
        <div className="hidden md:flex items-center gap-4">
          <h1 
            className="text-xl font-semibold text-stone-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {getPageTitle()}
          </h1>

          {/* Entity switcher */}
          {hasMultipleEntities && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors text-sm text-stone-600"
                  data-testid="entity-switcher"
                  disabled={switching}
                >
                  <Building2 className="w-3.5 h-3.5" />
                  <span className="max-w-[180px] truncate font-medium">{org?.company_name || 'Entity'}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-stone-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="text-xs text-stone-400 uppercase tracking-wider">Entities</DropdownMenuLabel>
                {entities.map(e => (
                  <DropdownMenuItem
                    key={e.id}
                    onClick={() => handleSwitchEntity(e.id)}
                    className="cursor-pointer flex items-center justify-between"
                    data-testid={`entity-option-${e.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                      <span className="truncate text-sm">{e.company_name}</span>
                    </div>
                    {e.id === org?.id && <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                  </DropdownMenuItem>
                ))}
                {canCreateEntity && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setShowNewEntity(true)}
                      className="cursor-pointer text-stone-500"
                      data-testid="create-entity-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-2" />
                      New entity
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Mobile: hamburger */}
        <div className="md:hidden">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="mobile-menu-btn">
                <Menu className="w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <MobileNav onClose={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>

        {/* Right side — notifications + account dropdown */}
        <div className="flex items-center gap-2">
          <NotificationBell />
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hidden md:flex items-center gap-2.5 rounded-full hover:bg-stone-50 pl-3 pr-1.5 py-1.5 transition-colors" data-testid="account-menu-trigger">
              <span className="text-sm font-medium text-stone-700">{user?.name}</span>
              <UserInitials name={user?.name} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <p className="text-sm font-semibold text-stone-900 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500 truncate">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer" data-testid="menu-settings">
              <Settings className="w-4 h-4 mr-2" />
              Account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50" data-testid="menu-logout">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>
    </header>

    {/* New Entity Dialog */}
    <Dialog open={showNewEntity} onOpenChange={setShowNewEntity}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>New Legal Entity</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-xs font-semibold text-stone-600">Company Name</Label>
          <Input
            value={newEntityName}
            onChange={e => setNewEntityName(e.target.value)}
            placeholder="e.g. Acme Holdings GmbH"
            className="mt-1"
            data-testid="new-entity-name"
          />
          <p className="text-xs text-stone-400 mt-2">The new entity will inherit your current country and pricing tier.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowNewEntity(false)}>Cancel</Button>
          <Button
            onClick={handleCreateEntity}
            disabled={creatingEntity}
            className="bg-stone-900 hover:bg-stone-800 text-white"
            data-testid="submit-new-entity"
          >
            {creatingEntity ? 'Creating...' : 'Create Entity'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
